<template>
	<button
		class="app-button"
		:class="[
			`app-button-${buttonSize} app-button-${icon}`,
			{
				'app-button--loading': loading,
				'app-button--disabled': disabled,
			},
		]"
		:disabled="disabled"
		@click="handleClick"
	>
		<div v-if="loading" class="app-button__loading">
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="24"
				height="24"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				stroke-width="2"
				stroke-linecap="round"
				stroke-linejoin="round"
				class="feather feather-loader"
			>
				<line x1="12" y1="2" x2="12" y2="6" />
				<line x1="12" y1="18" x2="12" y2="22" />
				<line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
				<line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
				<line x1="2" y1="12" x2="6" y2="12" />
				<line x1="18" y1="12" x2="22" y2="12" />
				<line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
				<line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
			</svg>
		</div>
		<SideArrow
			v-else-if="
				icon === IconTypes.ARROW_LEFT || icon === IconTypes.ARROW_RIGHT
			"
			:type="icon === IconTypes.ARROW_LEFT ? 'left' : 'right'"
		/>
		<AppControlIcon v-else :iconName="icon" :size="computedIconSize" />
		<AppNotification
			v-if="notification"
			:value="notification"
			:type="StatusTypes.WARNING"
		/>
	</button>
</template>

<script lang="ts" setup>
import AppControlIcon from "@/components/AppControlIcon.vue";
import AppNotification from "@/components/AppNotification.vue";
import SoundService from "@/game/SoundService";
import { IconTypes, StatusTypes } from "@/types/ui";
import { computed, markRaw, ref, watch } from "vue";
import SideArrow from "./SVG/SideArrow.vue";

interface Props {
	disabled?: boolean;
	loading?: boolean;
	type?: IconTypes;
	size?: number;
	iconSize?: number;
	notification?: number | string;
}

const {
	disabled = false,
	loading = false,
	type = IconTypes.CLOSE,
	size = null,
	iconSize = null,
	notification = null,
} = defineProps<Props>();

const soundService = markRaw(SoundService.getInstance());

const emit = defineEmits(["click"]);
const isActive = ref<boolean>(false);
const icon = ref<IconTypes>(type);

watch(
	(): IconTypes => type,
	(newType: IconTypes): IconTypes => (icon.value = newType),
	{ immediate: true },
);

const buttonSize = computed(
	(): number =>
		size ??
		{
			[icon.value]: 40,
			[IconTypes.CLOSE]: 40,
			[IconTypes.INFO]: 32,
			[IconTypes.FILTER]: 22,
			[IconTypes.SHOW]: 22,
			[IconTypes.HIDE]: 22,
			[IconTypes.GRID]: 40,
			[IconTypes.LIST]: 40,
			[IconTypes.PLAY]: 40,
			[IconTypes.EXPAND]: 40,
			[IconTypes.COLLAPSE]: 40,
			[IconTypes.COPY]: 28,
			[IconTypes.EDIT_LIGHT]: 28,
			[IconTypes.EDIT_DARK]: 28,
			[IconTypes.PLAY_AD]: 28,
			[IconTypes.SAVE]: 28,
			[IconTypes.STORAGE]: 28,
			[IconTypes.TIMER]: 28,
			[IconTypes.CENTER]: 40,
			[IconTypes.REWARD]: 40,
			[IconTypes.PUT_IN_INVENTORY]: 40,
			[IconTypes.SEARCH]: 40,
		}[icon.value],
);

const computedIconSize = computed(
	(): number =>
		iconSize ??
		{
			[icon.value]: 20,
			[IconTypes.CLOSE]: 20,
			[IconTypes.INFO]: 20,
			[IconTypes.FILTER]: 20,
			[IconTypes.SHOW]: 20,
			[IconTypes.HIDE]: 20,
			[IconTypes.GRID]: 20,
			[IconTypes.LIST]: 20,
			[IconTypes.PLAY]: 20,
			[IconTypes.EXPAND]: 20,
			[IconTypes.COLLAPSE]: 20,
			[IconTypes.COPY]: 16,
			[IconTypes.EDIT_LIGHT]: 16,
			[IconTypes.EDIT_DARK]: 16,
			[IconTypes.PLAY_AD]: 16,
			[IconTypes.SAVE]: 16,
			[IconTypes.STORAGE]: 16,
			[IconTypes.TIMER]: 16,
			[IconTypes.CENTER]: 26,
			[IconTypes.REWARD]: 26,
			[IconTypes.PUT_IN_INVENTORY]: 26,
			[IconTypes.SEARCH]: 26,
		}[icon.value],
);

const handleClick = (): void => {
	// TODO externalize functionality into ModalWindow
	if (icon.value === IconTypes.SHOW || icon.value === IconTypes.HIDE) {
		isActive.value = !isActive.value;
		// Toggle visibility icon based on the active state
		icon.value = isActive.value ? IconTypes.HIDE : IconTypes.SHOW;
		emit("click", icon.value);
	} else if (icon.value === IconTypes.CLOSE) {
		soundService.playClickBack();
		emit("click");
	} else {
		emit("click");
	}
};
</script>

<style lang="scss" scoped>
.app-button {
	position: relative;
	@include flex-row-center;
	@include clickable-element;
	filter: drop-shadow(0 0.1rem 0.1rem rgba(0, 0, 0, 0.5));

	// Types
	&-close {
		box-shadow: inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
		border-width: 0.075rem;
		border-style: solid;
		border-image-source: linear-gradient(to top, #bd9032, #f0d386);
		border-image-slice: 1;
		background-image: linear-gradient(to top, #590919, #91092a);
	}

	&-info {
		box-shadow: inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
		border-width: 0.075rem;
		border-style: solid;
		border-image-source: linear-gradient(to top, #bd9032, #f0d386);
		border-image-slice: 1;
		background-image: linear-gradient(to top, #092f59, #094891);
	}

	&-grid,
	&-list,
	&-edit-light,
	&-edit-dark,
	&-save,
	&-copy,
	&-reward,
	&-arrow-left,
	&-arrow-right,
	&-center {
		box-shadow: inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
		border-width: 0.075rem;
		border-style: solid;
		border-image-source: linear-gradient(to top, #bd9032, #f0d386);
		border-image-slice: 1;
		background-image: linear-gradient(to top, #181a1d, #303239);
	}

	&-play,
	&-search {
		box-shadow:
			0 0.063rem 0.25rem 0 rgba(0, 0, 0, 0.6),
			inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
		border-width: 0.075rem;
		border-style: solid;
		border-image-source: linear-gradient(to top, #bd9032, #f0d386);
		border-image-slice: 1;
		background-image: linear-gradient(to top, #1d5261, #2b91ad);
	}

	&-show,
	&-hide {
		border: none;
		background: transparent;
	}

	&-40 {
		width: 2.5rem;
		height: 2.5rem;
	}

	&-32 {
		width: 2rem;
		height: 2rem;
	}

	&-22 {
		width: 1.375rem;
		height: 1.375rem;
	}
	&-20 {
		width: 1rem;
		height: 1rem;
	}
	&-28 {
		width: 1.75rem;
		height: 1.75rem;
	}

	:deep(.notification) {
		position: absolute;
		top: -0.469rem;
		right: -0.469rem;
	}
}
</style>
